/**
 * A field an organisation defines for itself, on whatever it is defining it.
 *
 * Contacts have these, and so do segments. The model is the same in both: a
 * key a rule names, a label a person reads, a kind, and — for the kinds that
 * are chosen from a list — the options. What differs is only which names are
 * already taken, which is why that is a parameter rather than a constant here.
 *
 * The predecessor put these in the shared contacts table as columns —
 * `masters_student`, `level1_year`, `level2_year` — which is one customer's
 * spreadsheet compiled into the product. Every tenant carried them, and adding
 * one meant a migration.
 *
 * `key` is what a segment rule and a merge token name. `label` is what a person
 * reads. They are separate so that renaming "Cohort" to "Intake" does not
 * silently change what every saved segment asks about.
 */
/** Not exported: the union is the type, and this is what checks against it. */
const CONTACT_FIELD_TYPES = [
    'text',
    'number',
    'date',
    'boolean',
    'email',
    'phone',
    'url',
    'dropdown',
    'status',
];
/** The kinds that are one of a list, and so need one. */
const CHOSEN_FROM = ['dropdown', 'status'];
export function needsChoices(type) {
    return CHOSEN_FROM.includes(type);
}
/**
 * How many options a column may offer, and how long one may be.
 *
 * A limit is needed because options are created as data arrives: a free-text
 * column somebody typed as a dropdown by mistake would otherwise grow one
 * option per row, and forty thousand of them is a column nobody can use and a
 * listing too big to draw.
 *
 * The two numbers are also what `choicesSchema` in `packages/contracts`
 * allows. They have to agree — a stored field wider than the contract is one
 * the browser refuses to parse, which breaks the whole field listing rather
 * than the one column — and `choicesCapMatchesContract.test.ts` in
 * `packages/adapter-http` is what holds them together, because that is the
 * package that can see both.
 */
export const MAX_CHOICES = 50;
export const MAX_CHOICE_LENGTH = 60;
/**
 * Which of these values a column does not yet offer.
 *
 * The owner asked to declare a column's name and kind and nothing else, and
 * have the options appear as data arrives. This is that rule, and it is
 * deliberately blind to which column it is looking at: a branch per column name
 * is how the predecessor grew a product that only its first customer fitted.
 *
 * Matching is case-insensitive on the trimmed value, the same comparison
 * `validateDeclaredField` refuses a duplicate by — otherwise importing "gold"
 * beside an existing "Gold" would add an option nobody can tell apart from the
 * one already there, and leave the column unsaveable from its own dialog.
 *
 * A kind that is not chosen from a list gains nothing: its values are free
 * text, and a list of them would be a promise the board does not keep.
 */
export function newChoicesFor(
/** Only the two parts that decide the answer, so a caller holding a row need not invent a label. */
field, values) {
    if (!needsChoices(field.type))
        return { add: [], noRoom: 0 };
    const known = new Set(field.choices.map((choice) => choice.trim().toLowerCase()));
    const room = Math.max(MAX_CHOICES - field.choices.length, 0);
    const add = [];
    let noRoom = 0;
    for (const value of values) {
        const choice = value.trim();
        if (choice === '')
            continue;
        const seen = choice.toLowerCase();
        if (known.has(seen))
            continue;
        known.add(seen);
        if (choice.length > MAX_CHOICE_LENGTH || add.length === room) {
            noRoom += 1;
            continue;
        }
        add.push(choice);
    }
    return { add, noRoom };
}
/**
 * What a key may be.
 *
 * Narrow on purpose, and not only for tidiness: a key reaches SQL as a bound
 * parameter in a jsonb lookup and reaches an email as a merge placeholder, and
 * the placeholder grammar is `[a-zA-Z0-9_.-]`. A key outside that would be
 * storable, rule-able, and silently unrenderable — `{{my key}}` would reach a
 * recipient as itself.
 *
 * Leading digits are refused so a key cannot read as an array index, and the
 * reserved names are refused because `renderMessage` fills those from the
 * recipient: a field keyed `name` would be shadowed at send and never appear.
 */
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/**
 * Whether a field can be created, and what is wrong if not.
 *
 * Returns a problem rather than throwing, because the caller is a form.
 *
 * `reserved` is what this subject already fills in for itself: a contact's are
 * the merge tokens `renderMessage` writes from the recipient, and a segment's
 * are its own board columns. A field claiming one of them would save happily
 * and then never appear, because the built-in wins.
 */
export function validateDeclaredField(field, reserved) {
    if (!KEY_PATTERN.test(field.key)) {
        return {
            reason: 'a field key is lowercase letters, digits and underscores, starting with a letter — ' +
                `"${field.key}" is not`,
        };
    }
    if (reserved.includes(field.key)) {
        return {
            reason: `"${field.key}" is filled in already, so a field cannot be called that`,
        };
    }
    if (field.label.trim() === '')
        return { reason: 'a field needs a label' };
    if (needsChoices(field.type)) {
        /*
         * No options is allowed, and is the ordinary way to start one.
         *
         * It used to be refused as useless, and that was true while the only way to
         * get an option was to type it. Since an import creates the ones it meets,
         * a column declared with a name and a kind and nothing else fills itself —
         * which is what the owner asked for on 2026-09-17.
         */
        const named = field.choices.filter((choice) => choice.trim() !== '');
        if (new Set(named.map((choice) => choice.trim().toLowerCase())).size !== named.length) {
            // Two choices that read the same are two a person cannot tell apart, and
            // a rule asking for one of them would be a rule nobody can reason about.
            return { reason: 'two of those choices are the same' };
        }
    }
    else if (field.choices.length > 0) {
        return { reason: `a ${field.type} field is not chosen from a list` };
    }
    if (!CONTACT_FIELD_TYPES.includes(field.type)) {
        return { reason: `"${field.type}" is not a kind of field` };
    }
    return undefined;
}
/**
 * Turns a label into a usable key.
 *
 * Offered to a form so somebody typing "Renewal date" gets `renewal_date`
 * rather than having to know what a key is. It can still produce something
 * `validateDeclaredField` refuses — an all-punctuation label, or a reserved
 * word — and that refusal is the form's to show. Guessing a fallback here
 * would hand somebody a field named something they never chose.
 */
export function keyFromLabel(label) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
}
//# sourceMappingURL=declaredField.js.map